/**
 * Sprint 23.8 — WorkspaceIdentityStrip (hero section 1).
 *
 * Workspace name (inline-editable via `update_workspace_name`) + mission
 * subtitle (inline-editable via the `mission` workspace_context key) + a meta
 * row (agents · members · plan · since) + the action row (Hire · New task ·
 * Edit + dots). Members see it read-only (Q26 parity with the agent profile).
 */

import { useState, type CSSProperties } from "react";
import { useNavigate } from "react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { EditableTitle, showToast } from "../primitives";
import { ActionButtonRow, type ActionButtonRowAction } from "../shell/ActionButtonRow";
import type { ActionMenuItem } from "../shell/ActionMenu";
import { InlinePersonaEdit } from "../persona/InlinePersonaEdit";
import { SetTaskModal } from "./SetTaskModal";
import { useThemedLabels } from "../../hooks/use-themed-labels";
import { useWorkspaceOverview } from "../../hooks/use-workspace-overview";
import { useUpdateWorkspaceName } from "../../../lib/hooks/use-kuvio";
import { mcpClient } from "../../../lib/mcp-client";
import { formatSince } from "./format";

function capitalize(s: string): string {
  return s.length ? s[0]!.toUpperCase() + s.slice(1) : s;
}

export interface WorkspaceIdentityStripProps {
  /** Owner gate — members see name/mission/actions read-only. */
  canEdit: boolean;
}

export function WorkspaceIdentityStrip({ canEdit }: WorkspaceIdentityStripProps) {
  const navigate = useNavigate();
  const labels = useThemedLabels();
  const qc = useQueryClient();
  const { data } = useWorkspaceOverview();
  const updateName = useUpdateWorkspaceName();
  const [taskOpen, setTaskOpen] = useState(false);

  const saveMission = useMutation({
    mutationFn: (value: string) => mcpClient.setWorkspaceContext("mission", value),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["workspace-overview"] }),
  });

  const name = data?.workspace?.name ?? "Workspace";
  const mission = data?.mission ?? null;

  const actions: ActionButtonRowAction[] = canEdit
    ? [
        { label: labels.addAction, icon: "plus", onClick: () => navigate("/hire") },
        { label: "New task", icon: "checklist", onClick: () => setTaskOpen(true) },
        { label: "Edit", icon: "edit", onClick: () => navigate("/settings/workspace") },
      ]
    : [];

  const menuItems: ActionMenuItem[] = canEdit
    ? [
        { label: "Manage members", icon: "users", onClick: () => navigate("/settings/members") },
        { label: "Billing", icon: "credit-card", onClick: () => navigate("/settings/workspace") },
        { label: "Export", icon: "download", onClick: () => showToast("Export is coming soon.") },
        {
          label: "Archive workspace",
          icon: "archive",
          variant: "danger",
          onClick: () => showToast("Archive a workspace from Workspace settings.", "error", true),
        },
      ]
    : [];

  return (
    <div style={container}>
      <div style={leftCol}>
        {canEdit ? (
          <EditableTitle
            value={name}
            fontSize={22}
            fontWeight={500}
            onSave={async (newName) => {
              try {
                await updateName.mutateAsync(newName);
                qc.invalidateQueries({ queryKey: ["workspace-overview"] });
                showToast(`Renamed to '${newName}'`);
              } catch (err) {
                showToast(
                  `Couldn't rename: ${err instanceof Error ? err.message : "error"}`,
                  "error",
                  true,
                );
                throw err;
              }
            }}
          />
        ) : (
          <h1 style={nameStatic}>{name}</h1>
        )}

        <div style={missionRow}>
          <InlinePersonaEdit
            label="Mission"
            hideLabel
            value={mission}
            mode="text"
            readOnly={!canEdit}
            placeholder={labels.workspaceMissionPlaceholder}
            renderValue={(v) =>
              typeof v === "string" && v.trim().length > 0 ? (
                <span style={missionText}>{v}</span>
              ) : (
                <span style={missionPlaceholder}>{labels.workspaceMissionPlaceholder}</span>
              )
            }
            onSave={async (next) => {
              await saveMission.mutateAsync(typeof next === "string" ? next.trim() : "");
            }}
          />
        </div>

        <div style={metaRow}>
          <span>
            {data?.agents_count ?? 0} {labels.unitPlural}
          </span>
          <Sep />
          <span>{data?.members_count ?? 0} members</span>
          <Sep />
          <span>{capitalize(data?.plan ?? "free")} plan</span>
          <Sep />
          <span>since {formatSince(data?.workspace?.created_at)}</span>
        </div>
      </div>

      {(actions.length > 0 || menuItems.length > 0) && (
        <div style={{ flexShrink: 0 }}>
          <ActionButtonRow actions={actions} menuItems={menuItems} dense />
        </div>
      )}

      <SetTaskModal open={taskOpen} onClose={() => setTaskOpen(false)} />
    </div>
  );
}

function Sep() {
  return <span style={{ opacity: 0.5 }}>·</span>;
}

const container: CSSProperties = {
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: 12,
};

const leftCol: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 4,
  minWidth: 0,
};

const nameStatic: CSSProperties = {
  fontSize: 22,
  fontWeight: 500,
  margin: 0,
  color: "var(--color-text-primary)",
};

const missionRow: CSSProperties = {
  minWidth: 0,
};

const missionText: CSSProperties = {
  fontSize: 13,
  color: "var(--color-text-secondary)",
  lineHeight: 1.5,
};

const missionPlaceholder: CSSProperties = {
  fontSize: 13,
  color: "var(--color-text-tertiary)",
  fontStyle: "italic",
  lineHeight: 1.5,
};

const metaRow: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  fontSize: 11,
  color: "var(--color-text-tertiary)",
  marginTop: 2,
  flexWrap: "wrap",
};
